import { useState, useEffect, useContext, useRef, useImperativeHandle } from "react";
import { useSelector } from "react-redux";
import { createPortal } from "react-dom";
import CxfMonacoEditor from "components/CxfMonacoEditor";
import { transpile, ModuleKind } from "typescript";
import { ComponentPropsContext, ComponentPropsContextApi } from "layouts/Flow Designer/context";
import useTestCode from "./hooks/useTestCode";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Divider from "@mui/material/Divider";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { ListItemIcon } from "layouts/Flow Designer/components/Titlebar/components/TitlebarActions/StyledComponents";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import axios from "axios";
import axiosTypeDefs from "./libsTypeDefs/axios";
import { useVerticalResizer } from "hooks/useVerticalResizer";
import { useCodeEditorColumnResizer } from "hooks/useCodeEditorColumnResizer";
import { THEME_TYPES, THEME_COLORS } from "constants/theme";
import { EditorExpansion } from "../../../EditorExpansion";
import { useDebouncer } from "hooks/useDebouncer";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ListItemButton from "@mui/material/ListItemButton";
import { ListItemText } from "layouts/Flow Designer/components/Titlebar/components/TitlebarActions/StyledComponents";
import LanguageSelect from "../CodeEditorLanguageListItem";

const EditorOptions = {
  minimap: {
    enabled: false
  },
  scrollbar: {
    vertical: "auto",
    verticalScrollbarSize: 8
  },
  renderLineHighlight: "none",
  wordWrap: "on",
  formatOnType: true,
  padding: {
    top: 10
  },
  fontSize: 12,
  lineHeight: 15,
  hover: {
    above: false,
    sticky: true
  },
  inlayHints: {
    padding: true
  }
};

const outpuOrientation = {
  bottom: "bottom",
  right: "right"
};

const horizontalOrientationHeight = "calc(100vh - 430px)";

const CodeEditorInput = ({ action, fieldValue, allowEditWorkflow, ref }) => {
  const isDark = useSelector((store) => store?.portal?.settings.themeMode) === THEME_TYPES.DARK;
  const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
  const isFlowActive = currentSavedFlow?.shared_status === "active";
  const { actionForm } = useContext(ComponentPropsContext);
  const { setActionForm } = useContext(ComponentPropsContextApi);
  const { context } = useSelector((store) => store?.designer?.editorContext);
  const languageOptions = [
    { value: "javascript", label: "JavaScript" },
    { value: "typescript", label: "Typescript" }
  ];
  const initialLanguage =
    actionForm["language"]?.value &&
    ["javascript", "typescript"].includes(actionForm["language"].value.toLowerCase())
      ? actionForm["language"].value.toLowerCase()
      : "javascript";
  const [language, setLanguage] = useState(initialLanguage);
  const handleLanguageChange = (selected) => {
    const lang = selected.value.toLowerCase();
    setLanguage(lang);
    setActionForm((prev) => ({
      ...prev,
      language: { ...prev.language, value: lang }
    }));
    if (editorRef.current && monacoRef.current) {
      monacoRef.current.editor.setModelLanguage(editorRef.current.getModel(), lang);
    }
  };
  const [logs, setLogs] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [orientation, setOrientation] = useState(outpuOrientation.bottom);
  const { runTestCode } = useTestCode();
  const monacoRef = useRef(null);
  const editorRef = useRef(null);
  const outputContainerRef = useRef(null);
  const { startResizing, verticalHeight, setVerticalHeight } = useVerticalResizer({
    defaultHeight: 650,
    minHeight: 200
  });
  const {
    startResizing: startResizingLogs,
    verticalHeight: verticalHeightLogs,
    setVerticalHeight: setVerticalHeightLogs
  } = useVerticalResizer({ defaultHeight: 300, minHeight: 200 /*maxHeight: 600*/ });
  const { startResizing: startResizingColumn, horizontalWidth } = useCodeEditorColumnResizer();
  const [previousContainerHeights, setPreviousContainerHeight] = useState([400, 400]);
  const hasMounted = useRef(false);
  const [editorExpanded, setEditorExpanded] = useState(false);
  const [editorValue, setEditorValue] = useState(fieldValue);
  const { debouncer } = useDebouncer(handleSetActionForm, 100);

  function handleSetActionForm(value) {
    setActionForm((prev) => ({
      ...prev,
      [action.field]: {
        ...prev[action.field],
        value: value.code,
        transpiledCode: value.transpileCodeResult
      }
    }));
  }

  const isVerticalOrientation = () => {
    return orientation === outpuOrientation.bottom;
  };

  const addLibToMonaco = async (monaco) => {
    try {
      let contextSource = `
      declare const context: ${generateTypeDefinitions(context)};
      declare const Rest: ${generateRestTypeDefinition()};
      declare const Redis: ${generateRedisTypeDefinition()};
      declare const Session: ${generateSessionTypeDefinition()};
      declare const settings: typeof context['settings'];
      declare const flow_configuration: typeof context['flow_configuration'];
      declare const shared_flow_configurations: typeof context['shared_flow_configurations'];
      declare const flow_name: typeof context['flow_name'];
      declare const steps: typeof context['steps'];
      declare const triggers: typeof context['triggers'];
      declare const variables: typeof context['variables'];
      declare const tenant_id: typeof context['tenant_id'];
      declare const integration_ids: typeof context['integration_ids'];
      declare const flow_id: typeof context['flow_id'];
      declare const ReturnValue: ${generateReturnValueTypeDefinition()};
      declare const invocation: typeof context['invocation'];
      declare function require(moduleName: string): any;
      `;
      let contextUri = "ts:filename/global.d.ts";

      const libraries = [
        { source: contextSource, uri: contextUri },
        {
          source: axiosTypeDefs,
          uri: "ts:filename/axios.d.ts"
        }
      ];

      let currentLanguage = actionForm["language"]?.value || "javascript";
      libraries.forEach((lib) => {
        let existingModel = monaco?.editor?.getModel(monaco.Uri.parse(lib.uri));

        if (existingModel) {
          // Update the content of the existing model
          existingModel.setValue(lib.source);
          // Update the language of the existing model
          monaco?.editor?.setModelLanguage(existingModel, currentLanguage);
        } else {
          // Create a new model and add it to the editor
          if (currentLanguage === "typescript") {
            monaco.languages?.typescript?.typescriptDefaults?.addExtraLib(lib.source, lib.uri);
          } else {
            monaco.languages?.typescript?.javascriptDefaults?.addExtraLib(lib.source, lib.uri);
          }
          monaco?.editor?.createModel(lib.source, "typescript", monaco.Uri?.parse(lib.uri));
        }
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleEditorDidMount = (editor, monaco) => {
    monacoRef.current = monaco;
    editorRef.current = editor;
    const diagnosticsOptions = { diagnosticCodesToIgnore: [80001] };
    monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions(diagnosticsOptions);
    monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions(diagnosticsOptions);
    addLibToMonaco(monaco);
  };

  const transpileCode = (code) => {
    try {
      let transpiledResult = transpile(code, {
        compilerOptions: { module: ModuleKind.CommonJS }
      });

      return transpiledResult;
    } catch (e) {
      setLogs((prev) => [...prev, { message: e.message, isError: true }]);
    }
  };

  const handleDebounceValue = (value) => {
    if (isFlowActive || !allowEditWorkflow) return;

    let code = value;
    let transpileCodeResult = actionForm[action.field]?.transpiledCode || "";
    let maxCodeLength = 400000;

    if (code.length >= maxCodeLength) {
      setLogs((prev) => [
        ...prev,
        {
          message: `The maximum characters length exceeded. Maximum allowed length is ${maxCodeLength} characters.`,
          isError: true
        }
      ]);
      return;
    }

    if (language === "typescript") {
      transpileCodeResult = transpileCode(code);
    }

    setEditorValue(code);

    debouncer({
      code,
      transpiledCode: transpileCodeResult
    });
  };

  const testCode = async () => {
    if (isFlowActive || !allowEditWorkflow) return;

    try {
      const model = editorRef.current?.getModel();
      const markers = monacoRef?.current?.editor?.getModelMarkers({
        resource: model?.uri
      })

      if (markers.length) {
        let newErrorMessages = handleEditorMarkers(markers);
        setLogs((prev) => [...prev, ...newErrorMessages]);
        return;
      }

      const currentCode = editorRef.current?.getValue() ?? editorValue ?? "";

      if (language === "typescript") {
        let transpilCodeResult = transpileCode(currentCode);
        runTestCode(setLogs, transpilCodeResult, context, axios);
        return;
      }

      const globals = `
      const flow = context;
      `;
      const userCode = currentCode;
      const fullCode = `${globals}\n${userCode}`;

      runTestCode(setLogs, fullCode, context, axios);
    } catch (error) {
      console.log(error);
    }
  };

  const handleEditorMarkers = (markers) => {
    let newErrorMessages = [];

    markers?.forEach((marker) => {
      const lineNumber = marker.startLineNumber;
      const column = marker.startColumn;
      const message = marker.message;

      // Construct error message
      newErrorMessages.push({
        message: `Error at line ${lineNumber}, column ${column}: ${message}`,
        isError: true
      });
    });

    if (actionForm[action.field]?.errors || newErrorMessages.length > 0) {
      setActionForm((prev) => ({
        ...prev,
        [action.field]: { ...prev[action.field], errors: newErrorMessages }
      }));
    }

    return newErrorMessages;
  };

  const renderOutput = (data) => {
    return (
      <List>
        {data?.map(({ message, logType }, index) => {
          return (
            <div key={`output-${index}`}>
              <ListItem sx={{ my: "8px", wordBreak: "break-word" }}>
                <SoftTypography
                  key={index}
                  variant="caption"
                  color={determineLogColor(logType)}
                  sx={{ lineHeight: 1.5 }}
                >
                  {message}
                </SoftTypography>
              </ListItem>
              <Divider
                key={`divider-${index}`}
                sx={{
                  margin: "0 0",
                  borderColor: "#a5adc1 !important"
                }}
              />
            </div>
          );
        })}
      </List>
    );
  };

  const handleTestButton = () => {
    if (isFlowActive || !allowEditWorkflow) return;

    testCode();
  };

  useImperativeHandle(ref, () => ({
    triggerCodeEditorTestButton: handleTestButton
  }));

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  ``;
  useEffect(() => {
    if (isVerticalOrientation() && logs.length > 0) {
      outputContainerRef.current?.focus();
      outputContainerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest"
      });
    }
  }, [logs, orientation]);

  useEffect(() => {
    if (hasMounted.current) {
      if (isVerticalOrientation()) {
        setVerticalHeight(previousContainerHeights[0]);
        setVerticalHeightLogs(previousContainerHeights[1]);
      } else {
        setPreviousContainerHeight([verticalHeight, verticalHeightLogs]);
        setVerticalHeight(horizontalOrientationHeight);
        setVerticalHeightLogs(horizontalOrientationHeight);
      }
    }

    hasMounted.current = true;
  }, [orientation]);

  useEffect(() => {
    const formLang = actionForm["language"]?.value;
    if (
      formLang &&
      ["javascript", "typescript"].includes(formLang.toLowerCase()) &&
      formLang.toLowerCase() !== language
    ) {
      setLanguage(formLang.toLowerCase());
      // Dynamically update Monaco editor language if mounted
      if (editorRef.current && monacoRef.current) {
        monacoRef.current.editor.setModelLanguage(
          editorRef.current.getModel(),
          formLang.toLowerCase()
        );
      }
    }
    if (!formLang) {
      setLanguage("javascript");
      setActionForm((prev) => ({
        ...prev,
        language: { ...prev.language, value: "javascript" }
      }));
    }
    if (actionForm[action.field]?.value && formLang && formLang.toLowerCase() === "typescript") {
      let transpilCodeResult = transpileCode(actionForm[action.field]?.value);
      setActionForm((prev) => ({
        ...prev,
        [action.field]: { ...prev[action.field], transpiledCode: transpilCodeResult }
      }));
    }
    if (monacoRef?.current) {
      addLibToMonaco(monacoRef?.current);
    }
  }, [actionForm["language"]?.value]);

  useEffect(() => {
    if (monacoRef.current) {
      let markers = monacoRef.current?.editor?.getModelMarkers();
      handleEditorMarkers(markers);
    }
  }, [monacoRef.current]);

  useEffect(() => {
    if (monacoRef.current) {
      addLibToMonaco(monacoRef.current);
    }
  }, [context]);

  useEffect(() => {
    if (editorExpanded) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100vw";
      document.body.style.height = "100vh";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.height = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.height = "";
    };
  }, [editorExpanded]);

  useEffect(() => {
    if (editorExpanded) {
      const handleResize = () => {
        if (editorRef.current) editorRef.current.layout();
      };
      window.addEventListener("resize", handleResize);
      window.addEventListener("orientationchange", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("orientationchange", handleResize);
      };
    }
  }, [editorExpanded]);

  const handleExpansion = () => {
    setEditorExpanded((prev) => {
      const newExpanded = !prev;
      if (!prev && editorRef.current) {
        setTimeout(() => editorRef.current.layout(), 100);
      }
      return newExpanded;
    });
  };

  return (
    <>
      {editorExpanded &&
        createPortal(
          <SoftBox
            sx={{
              position: "fixed",
              top: "env(safe-area-inset-top, 0)",
              left: "env(safe-area-inset-left, 0)",
              right: "env(safe-area-inset-right, 0)",
              bottom: "env(safe-area-inset-bottom, 0)",
              width: "100vw",
              height: "100vh",
              zIndex: 9999,
              backgroundColor: isDark ? THEME_COLORS.DARK.PRIMARY_BG : THEME_COLORS.LIGHT.PRIMARY_BG
            }}
          >
            <div
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
            >
              <CxfMonacoEditor
                height="100vh"
                width="100vw"
                language={language}
                defaultValue={editorValue}
                options={{
                  ...EditorOptions,
                  domReadOnly: isFlowActive || !allowEditWorkflow,
                  readOnly: isFlowActive || !allowEditWorkflow
                }}
                onMount={handleEditorDidMount}
                onChange={isFlowActive ? undefined : handleDebounceValue}
                onValidate={handleEditorMarkers}
                editorRef={editorRef}
              />
            </div>
            <EditorExpansion isExpanded={editorExpanded} handleExpansion={handleExpansion} />
          </SoftBox>,
          document.body
        )}
      {!editorExpanded && (
        <SoftBox width="100%" height="100%">
          <Grid container spacing={2}>
            <div
              style={{
                display: "flex",
                flexDirection: isVerticalOrientation() ? "column" : "row",
                width: "100%",
                height: "100%"
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: isVerticalOrientation()
                    ? "100%"
                    : `calc(50% - (-1 * ${horizontalWidth}px))`,
                  minWidth: "200px"
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%"
                  }}
                >
                  <SoftTypography
                    variant="h6"
                    style={{
                      fontWeight: "bold",
                      padding: isVerticalOrientation() ? "20px 0 7px 0" : "0 0 7px 0",
                      marginRight: 12
                    }}
                  >
                    {"Editor"}
                  </SoftTypography>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <ListItemButton
                      onClick={handleTestButton}
                      disabled={isFlowActive || !allowEditWorkflow}
                      sx={{
                        borderRadius: "9px",
                        minHeight: 0,
                        height: 34,
                        padding: "0 8px",
                        marginRight: "8px"
                      }}
                    >
                      <ListItemIcon>
                        <PlayCircleOutlineIcon sx={{ mt: "1.5px" }} />
                        <ListItemText
                          primary="Test"
                          sx={{ alignItems: "center", display: "flex" }}
                        />
                      </ListItemIcon>
                    </ListItemButton>
                    <LanguageSelect
                      language={language}
                      languageOptions={languageOptions}
                      disabled={isFlowActive || !allowEditWorkflow}
                      onLanguageChange={handleLanguageChange}
                    />
                  </div>
                </div>
                <Grid size={{ xs: 12 }}>
                  <SoftBox
                    sx={{
                      inset: editorExpanded ? "0" : "initial",
                      position: editorExpanded ? "fixed" : "relative",
                      ...(editorExpanded ? { zIndex: 999 } : {})
                    }}
                  >
                    <div
                      onKeyDown={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <CxfMonacoEditor
                        height={editorExpanded ? "100vh" : verticalHeight}
                        language={language}
                        defaultValue={editorValue}
                        options={{
                          ...EditorOptions,
                          domReadOnly: isFlowActive || !allowEditWorkflow,
                          readOnly: isFlowActive || !allowEditWorkflow
                        }}
                        onMount={handleEditorDidMount}
                        onChange={isFlowActive ? undefined : handleDebounceValue}
                        onValidate={handleEditorMarkers}
                      />
                    </div>
                    <EditorExpansion
                      isExpanded={editorExpanded}
                      handleExpansion={() => setEditorExpanded((prev) => !prev)}
                    />
                    {isVerticalOrientation() && (
                      <SoftBox
                        className="app-horizontal-resizer shown"
                        onMouseDown={startResizing}
                      />
                    )}
                  </SoftBox>
                </Grid>
              </div>
              {orientation === "right" && (
                <SoftBox
                  className="app-sidebar-resizer shown"
                  position="static"
                  marginTop="28px"
                  height={verticalHeight}
                  onMouseDown={startResizingColumn}
                />
              )}
              {logs.length > 0 && (
                <SoftBox
                  position="relative"
                  width={orientation === "bottom" ? "100%" : `calc(50% - (${horizontalWidth}px))`}
                  minWidth="200px"
                  paddingBottom="16px"
                  ref={outputContainerRef}
                >
                  <SoftTypography
                    variant="h5"
                    style={{
                      fontSize: "15px",
                      padding: isVerticalOrientation() ? "20px 0 7px 0" : "0 0 7px 0"
                    }}
                    tabIndex={-1}
                  >
                    {"Output"}
                  </SoftTypography>
                  <Grid size={{ xs: 12 }} position="relative">
                    <Paper
                      className="custom-light-theme"
                      sx={{
                        padding: 2,
                        height: verticalHeightLogs,
                        overflow: "auto",
                        backgroundColor: "#ededed"
                      }}
                      id="ouput-container"
                    >
                      {renderOutput(logs)}
                      <ListItemIcon
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "10px",
                          cursor: "pointer"
                        }}
                        onClick={handleOpenMenu}
                      >
                        <MoreHorizIcon
                          sx={{
                            width: "25px",
                            "&:hover": {
                              backgroundColor: "#393939",
                              borderRadius: "3px"
                            }
                          }}
                        />
                      </ListItemIcon>

                      <Menu
                        id="basic-menu"
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        MenuListProps={{
                          "aria-labelledby": "basic-button"
                        }}
                        onClose={handleCloseMenu}
                      >
                        <MenuItem
                          onClick={() => {
                            setOrientation(outpuOrientation.bottom);
                            setAnchorEl(null);
                          }}
                          selected={isVerticalOrientation()}
                          disabled={isVerticalOrientation()}
                        >
                          Bottom
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setOrientation(outpuOrientation.right);
                            setAnchorEl(null);
                          }}
                          selected={!isVerticalOrientation()}
                          disabled={!isVerticalOrientation()}
                        >
                          Right
                        </MenuItem>
                      </Menu>
                    </Paper>
                    {isVerticalOrientation() && (
                      <SoftBox
                        className="app-horizontal-resizer shown"
                        onMouseDown={startResizingLogs}
                      />
                    )}
                  </Grid>
                </SoftBox>
              )}
            </div>
          </Grid>
        </SoftBox>
      )}
    </>
  );
};

export default CodeEditorInput;

const generateTypeDefinitions = (json, indent = 0) => {
  let typeDefs = "{\n";

  for (let key in json) {
    let value = json[key];
    let type = typeof value;

    if (type === "object" && value !== null) {
      type = generateTypeDefinitions(value, indent + 2); // Recursively process objects
    } else if (type === "number") {
      type = "number";
    } else if (type === "string") {
      type = "string";
    } else if (type === "boolean") {
      type = "boolean";
    } else if (value === null) {
      type = "null";
    } else {
      type = "any"; // Fallback type
    }

    typeDefs += " ".repeat(indent + 2) + `"${key}": ${type},\n`;
  }

  typeDefs += " ".repeat(indent) + "}";

  return typeDefs;
};

const generateRestTypeDefinition = () => {
  return `{
    setHeaders(headers: Record<string, string>): void;
    setResponse(response: any): void;
    dispatch(): void;
  };`;
};

const generateReturnValueTypeDefinition = () => {
  return `{
  set(value: any): void;}`;
};

const generateRedisTypeDefinition = () => {
  return `(id: string) => {
    pub: (topic: string, message: string) => void;
  }`;
};

const generateSessionTypeDefinition = () => {
  return `{
    exists(sessionId: string): Promise<boolean>;
    create(sessionId: string, obj?: Record<string, any>, ttl?: number): Promise<boolean>;
    set(sessionId: string, obj: Record<string, any>, merge?: boolean): Promise<boolean>;
    get(sessionId: string): Promise<Record<string, any> | null>;
    delete(sessionId: string): Promise<boolean>;
  };`;
};

const determineLogColor = (logType) => {
  switch (logType) {
    case "error":
      return "error";
    case "warn":
      return "warning";
    case "info":
    case "log":
      return "text";
    default:
      return "text";
  }
};
